import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconAlertCircle, IconArrowDown, IconArrowUp, IconArrowsMove, IconCircleCheck, IconCopy, IconTrash, IconX } from '@tabler/icons-react';
import useArtEditor from '../state/useArtEditor';
import PageModels from '../gallery/PageModels';
import { placeOf } from '../gallery/pageRoles';
import TemplateDialog, { formatNameOf } from '../gallery/TemplateDialog';
import { ArtEditorContext } from './context';
import ToolRail from './components/ToolRail';
import ToolPanel from './components/ToolPanel';
import Stage, { nextZoom } from './components/Stage';
import Inspector from './components/Inspector';
import ActionRail from './components/ActionRail';
import ContextMenu from './components/ContextMenu';
import { layerMenuItems } from './components/Inspector/sections/LayerActions';
import { imageSource } from './components/ToolPanel/panels/PhotosPanel';
import { videoSource } from './components/ToolPanel/panels/VideosPanel';
import Timeline from './components/Timeline';
import VideoTrimDialog from './components/VideoTrimDialog';
import useExport from './hooks/useExport';
import usePlayback from './hooks/usePlayback';
import useShortcuts from './hooks/useShortcuts';
import { enter, gsap, reducedMotion } from './motion';
import { createImageLayer, createVideoLayer } from '../model/factory';
import { blankPage, canAddPage, emptyImagePlaces, findLayer, maxPages, modelPages, pageIndex, replaceImage, setImageFill, updateLayer, withoutVideos } from '../model/operations';
import { overflowingLayers } from '../model/geometry';
import { ASPECT_RATIOS, VIDEO_LIMITS, normalizeDocument, takesVideo } from '../model/schema';
import { backgroundClips, clipLength, insertVideo, pageVideos, retrimClip, roomFor, sequenceEnd } from '../model/timeline';
import { readVideoFile } from '../render/video';
import './styles.scss';

const noop = () => {};
// The settings dock's width (styles.scss keeps the same).
const DOCK_WIDTH = 280;
// 2.13.0: what the host stores as a video, unless it says otherwise.
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const VIDEO_MAX_BYTES = 300 * 1024 * 1024;

function Busy({ busy }) {
  const sweep = useRef(null);
  const veil = useRef(null);
  useLayoutEffect(() => {
    enter(veil.current, {}, { duration: 0.2 });
    if (reducedMotion()) return undefined;
    const animation = gsap.fromTo(sweep.current, { xPercent: -100 }, { xPercent: 100, duration: 1.6, ease: 'sine.inOut', repeat: -1 });
    return () => animation.kill();
  }, []);
  return <div ref={veil} className="art-editor__busy" role="status" aria-live="polite">
    <span ref={sweep} className="art-editor__sweep" aria-hidden="true" />
    <div className="art-editor__busy-pill">{busy.mark}<Text.span contrast={9}>{busy.label}</Text.span></div>
  </div>;
}

function Notices({ notices, onDismiss }) {
  const { t } = useTranslation('artEditor');
  return <div className="art-editor__notices">
    {notices.map(notice => <div key={notice.id} className={`art-editor__toast is-${notice.kind}`} ref={element => element && !element.dataset.shown && (element.dataset.shown = '1') && enter(element, { y: -6 })}
      role={notice.kind === 'error' ? 'alert' : 'status'}>
      <Text.caption contrast={8} left={notice.kind === 'error' ? <IconAlertCircle /> : <IconCircleCheck />}>{notice.text}</Text.caption>
      <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconX />} aria-label={t('dismiss')} onClick={() => onDismiss(notice.id)} />
    </div>)}
  </div>;
}

// The editor block. See ../README.md for the interface; in short, `value` is the art
// document (ginastee.art/1) and every edit comes back through `onChange`; the host keeps
// it (autosave, revisions) and provides the services the editor cannot own: images,
// brand kit, the template gallery (a carousel's new pages), export persistence, the chat.
export default function ArtEditorView({
  value, onChange, preview = null, readOnly = false, busy = null, placeholderRatio = '4:5', title, status, conflict, notice, highlight,
  brandKit, onSaveBrandKit, images, videos = null, gallery = null, profile, caption, onCaptionChange, actions, versions, close, panelPlacement = 'stage',
  onSelectionChange, onAskAI, imageGeneration = false, beforeExport, onExported, className = '',
}) {
  const { t } = useTranslation('artEditor');
  const locked = !!readOnly || !!busy || !!preview;
  const editor = useArtEditor({ value, onChange, onSelectionChange, readOnly: locked });
  const [scale, setScale] = useState(0);
  const [notices, setNotices] = useState([]);
  const exporter = useExport({ document: editor.document, preview, title, beforeExport, onExported });

  // An older version is viewed page by page too, starting at the page being edited.
  const [previewIndex, setPreviewIndex] = useState(0);
  useEffect(() => { setPreviewIndex(editor.pageIndex); }, [preview]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = useMemo(() => {
    if (!preview) return editor;
    const index = Math.min(previewIndex, preview.pages.length - 1);
    return { ...editor, document: preview, page: preview.pages[index], pageIndex: index, selection: [], selected: [], editing: null,
      cropping: null, canUndo: false, canRedo: false, select: noop, edit: noop, crop: noop,
      setPage: id => setPreviewIndex(pageIndex(preview, id)), stepPage: delta => setPreviewIndex(value => Math.max(0, Math.min(preview.pages.length - 1, value + delta))) };
  }, [editor, preview, previewIndex]);
  useEffect(() => { if (locked && editor.editing) editor.edit(null); }, [locked]); // eslint-disable-line react-hooks/exhaustive-deps
  // A highlight with a page (the host pointing at a place: the image question's slot) brings
  // that page to the stage, once per highlight.
  const highlightPage = highlight?.pageId || null;
  useEffect(() => {
    if (!highlightPage) return;
    if (preview) { if (preview.pages.some(page => page.id === highlightPage)) setPreviewIndex(pageIndex(preview, highlightPage)); }
    else if (editor.document?.pages.some(page => page.id === highlightPage)) editor.setPage(highlightPage);
  }, [highlight?.key, highlightPage, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const notify = useCallback((text, kind = 'error') => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotices(list => [...list.slice(-2), { id, kind, text }]);
    setTimeout(() => setNotices(list => list.filter(item => item.id !== id)), 5000);
  }, []);

  // Uploads go through the host (`images.upload`); each one lands in the art (or replaces
  // the target image, or fills the target shape) as soon as it is stored. `place: false`
  // only uploads (the logo).
  const latest = useRef(editor);
  latest.current = editor;
  const uploadImages = useCallback(async (files, { target = null, place = true, at = null } = {}) => {
    if (!images?.upload) return [];
    const uploaded = [];
    for (const file of files) {
      try {
        const image = await images.upload(file);
        uploaded.push(image);
        const current = latest.current;
        if (!place || !current.document) continue;
        if (target && target.type !== 'image') { current.apply(doc => ({ doc: setImageFill(doc, target.id, imageSource(image)), select: [target.id] }), { label: 'fill' }); continue; }
        if (target) { current.apply(doc => replaceImage(doc, target.id, imageSource(image)), { label: 'replace' }); continue; }
        const layer = createImageLayer(current.document, imageSource(image));
        current.add(at ? { ...layer, x: Math.round(at.x - layer.width / 2), y: Math.round(at.y - layer.height / 2) } : layer);
      } catch { notify(t('uploadFailed', { name: file.name })); }
    }
    return uploaded;
  }, [images, notify, t]);

  // "Salvar como modelo" (`gallery.saveArt`, else `gallery.create`): the art on the stage
  // becomes a personal template, filed under one of its format's gallery categories.
  const [savingTemplate, setSavingTemplate] = useState(null);
  const openSaveTemplate = useCallback(async () => {
    // A template keeps the design; the clips (2.13.0) are this art's.
    const doc = latest.current.document && withoutVideos(latest.current.document);
    if (!doc || !gallery?.create) return;
    setSavingTemplate({ document: doc, categories: [] });
    try {
      const categories = await gallery.categories({ format: doc.format });
      setSavingTemplate(current => (current?.document === doc ? { ...current, categories } : current));
    } catch { setSavingTemplate(current => (current ? { ...current, categories: [] } : current)); }
  }, [gallery]);
  const saveTemplate = useCallback(async fields => {
    await (gallery.saveArt || gallery.create)(fields);
    setSavingTemplate(null);
    notify(t('templateSaved'), 'success');
  }, [gallery, notify, t]);

  // Videos (2.13.0, `videos`, carousels only): a file sent (the Vídeos panel, dropped on the
  // art) is read here, opens the trim dialog at once and uploads meanwhile; the clip lands
  // when the creator confirms and the file is stored. A video picked among the creator's opens
  // the dialog as is; a clip's "Cortar" opens it on the part it plays.
  const playback = usePlayback(view.page);
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const videoOn = !!videos?.upload && !!view.document && takesVideo(view.document.format);
  const [trim, setTrim] = useState(null);
  const trimKey = useRef(0);
  // Each stored upload lists the creator's videos again (the Vídeos panel).
  const [videoUploads, setVideoUploads] = useState(0);
  const uploadVideo = useCallback((key, file, meta) => {
    const patch = upload => setTrim(current => (current?.key === key ? { ...current, ...upload } : current));
    patch({ upload: { status: 'uploading', progress: 0 } });
    videos.upload(file, { ...meta, onProgress: progress => patch({ upload: { status: 'uploading', progress } }) })
      .then(video => { patch({ upload: { status: 'done', progress: 1 }, asset: video }); setVideoUploads(count => count + 1); })
      .catch(() => patch({ upload: { status: 'error', progress: 0 } }));
  }, [videos]);
  const openVideo = useCallback(async ({ file = null, video = null, background = false }) => {
    if (lockedRef.current) return;
    if (video) { setTrim({ key: ++trimKey.current, mode: 'add', video, asset: video, background, upload: null }); return; }
    if (!file || !videos?.upload) return;
    const types = videos.mimeTypes || VIDEO_TYPES;
    const maxBytes = videos.maxBytes || VIDEO_MAX_BYTES;
    if (!types.includes(file.type)) { notify(t('videoTypeError')); return; }
    if (file.size > maxBytes) { notify(t('videoSizeError', { size: Math.round(maxBytes / 1024 / 1024) })); return; }
    let meta;
    try { meta = await readVideoFile(file); } catch { notify(t('videoUnreadable', { name: file.name })); return; }
    if (meta.duration > VIDEO_LIMITS.sourceSeconds[1]) { URL.revokeObjectURL(meta.url); notify(t('videoTooLong', { minutes: VIDEO_LIMITS.sourceSeconds[1] / 60 })); return; }
    if (meta.duration < VIDEO_LIMITS.minClipSeconds) { URL.revokeObjectURL(meta.url); notify(t('videoTooShort')); return; }
    const key = ++trimKey.current;
    // The dialog plays the local file while it uploads.
    setTrim({ key, mode: 'add', video: { url: meta.url, width: meta.width, height: meta.height, duration: meta.duration }, local: meta.url, file, meta, background, asset: null,
      upload: { status: 'uploading', progress: 0 } });
    uploadVideo(key, file, { width: meta.width, height: meta.height, duration: meta.duration, mimeType: file.type });
  }, [videos, notify, t, uploadVideo]);
  const trimVideo = useCallback(layer => {
    if (!layer?.source || lockedRef.current) return;
    setTrim({ key: ++trimKey.current, mode: 'edit', layerId: layer.id, video: { url: layer.source.url, width: layer.source.width, height: layer.source.height, duration: layer.source.duration },
      initial: layer.trim, muted: layer.muted, upload: null });
  }, []);
  const closeTrim = useCallback(() => setTrim(current => { if (current?.local) setTimeout(() => URL.revokeObjectURL(current.local), 1000); return null; }), []);
  // What the dialog's selection may last: a new clip what the page has left (after the
  // background's clips for a background one); a clip cut again, enough to end by 60 s with
  // the background clips after it (they move with its end).
  const trimLimits = (() => {
    const current = latest.current;
    if (!trim || !current.document || !current.page) return null;
    if (trim.mode === 'add') return { background: roomFor(current.page, current.document, { background: true }), element: VIDEO_LIMITS.pageSeconds };
    const layer = current.page.layers.find(item => item.id === trim.layerId);
    if (!layer) return { element: VIDEO_LIMITS.pageSeconds };
    const background = backgroundClips(current.page, current.document).includes(layer);
    return { element: background ? VIDEO_LIMITS.pageSeconds - sequenceEnd(current.page, current.document) + clipLength(layer) : VIDEO_LIMITS.pageSeconds - layer.at };
  })();
  const confirmTrim = useCallback(({ trim: cut, background, muted }) => {
    const current = latest.current;
    const doc = current.document;
    if (!trim || !doc) return;
    if (trim.mode === 'edit') {
      if (findLayer(doc, trim.layerId)) current.apply(next => updateLayer(retrimClip(next, trim.layerId, { trim: cut }), trim.layerId, { muted }), { label: 'trim' });
    } else if (trim.asset) {
      const layer = createVideoLayer(doc, videoSource(trim.asset), { trim: cut, muted });
      const at = background ? sequenceEnd(current.page, doc) : 0;
      current.apply(next => ({ doc: insertVideo(next, current.page.id, layer, { background }), select: [layer.id] }), { label: 'video' });
      // The playhead goes to where the new clip starts, so it shows.
      requestAnimationFrame(() => playback.seek(at));
    }
    closeTrim();
  }, [trim, closeTrim, playback]);

  // A brand color tap paints what is selected (or the page).
  const paintSelection = useCallback(hex => {
    const current = latest.current;
    const layer = current.selected.length === 1 ? current.selected[0] : null;
    if (!layer) { current.background({ type: 'solid', color: hex }); return; }
    if (layer.type === 'text') current.update(layer.id, { style: { color: hex, fill: null } }, { key: null });
    if (layer.type === 'icon') current.update(layer.id, { color: hex }, { key: null });
    if (layer.type === 'shape') current.update(layer.id, layer.shape === 'line' ? { stroke: { ...layer.stroke, color: hex } } : { fill: { type: 'solid', color: hex } }, { key: null });
  }, []);

  // The settings dock: the inspector's and the action rail's settings (a fill, the text
  // format, effects, the download) open beside the art, one at a time, never over it. A
  // new selection, or a run starting, closes what was open.
  const [dockNode, setDockNode] = useState(null);
  const [dockId, setDockId] = useState(null);
  const dock = useMemo(() => ({ node: dockNode, active: dockId, open: id => setDockId(id),
    close: id => setDockId(current => (id === undefined || current === id ? null : current)) }), [dockNode, dockId]);
  const selectionKey = view.selection.join(',');
  useEffect(() => { setDockId(null); }, [selectionKey, locked]);
  const dockRef = useRef(null);
  const docking = !!dockId;
  useLayoutEffect(() => {
    if (!docking || !dockRef.current || reducedMotion()) return undefined;
    const animation = gsap.fromTo(dockRef.current, { width: 0, opacity: 0 }, { width: DOCK_WIDTH, opacity: 1, duration: 0.24, ease: 'power2.out', clearProps: 'width,opacity' });
    return () => animation.kill();
  }, [docking]);

  // Right-click on a layer (on the art or its row in Layers) selects it and opens the same
  // actions as the inspector's ⋯ menu, at the pointer.
  const menu = useRef(null);
  const openLayerMenu = useCallback((layer, event) => {
    if (locked || !layer) return;
    event.preventDefault();
    latest.current.select([layer.id]);
    menu.current?.open(layerMenuItems(layer, latest.current, t), event.clientX, event.clientY);
  }, [locked, t]);

  // A carousel's pages (PageNavigator): a new page starts from a page model (2.10.0, the
  // catalog's static posts, in the brand kit's text styles with the creator's avatar and @)
  // or blank (on the background of the page it follows); a page's menu duplicates, moves or
  // deletes it.
  const multiPage = !!view.document && maxPages(view.document) > 1;
  const [adding, setAdding] = useState(null);
  // Where the pages being added go; null once the dialog closed, so a late pick adds nothing.
  const addingAt = useRef(null);
  addingAt.current = adding;
  const insertPages = useCallback(pages => {
    const at = addingAt.current;
    if (at !== null) latest.current.addPages(pages, at);
    addingAt.current = null;
    setAdding(null);
  }, []);
  const insertModel = useCallback(template => {
    const handle = profile?.handle ? `@${profile.handle.replace('@', '')}` : null;
    const avatar = brandKit?.avatar ? { kind: 'asset', asset_id: brandKit.avatar.asset_id, url: brandKit.avatar.url, width: brandKit.avatar.width, height: brandKit.avatar.height } : null;
    insertPages(modelPages(latest.current.document, normalizeDocument(template.document), { fonts: brandKit?.configured ? brandKit.fonts : null, avatar, handle }));
  }, [insertPages, brandKit, profile]);
  const addPageAt = useCallback(index => {
    const doc = latest.current.document;
    if (!doc || !canAddPage(doc)) return;
    if (gallery) setAdding(index);
    else latest.current.addPages([blankPage(doc, doc.pages[index - 1])], index);
  }, [gallery]);
  const openPageMenu = useCallback((page, x, y) => {
    const current = latest.current;
    const doc = current.document;
    if (!doc) return;
    const at = pageIndex(doc, page.id);
    menu.current?.open([
      { label: t('duplicatePage'), icon: <IconCopy />, disabled: !canAddPage(doc), onClick: () => current.duplicatePage(page.id) },
      { label: t('movePageUp'), icon: <IconArrowUp />, disabled: at === 0, onClick: () => current.movePage(page.id, at - 1) },
      { label: t('movePageDown'), icon: <IconArrowDown />, disabled: at === doc.pages.length - 1, onClick: () => current.movePage(page.id, at + 1) },
      { divider: true },
      { label: t('deletePage'), icon: <IconTrash />, variant: 'error', disabled: doc.pages.length < 2, onClick: () => current.removePage(page.id) },
    ], x, y);
  }, [t]);
  const pages = multiPage && view.page ? { doc: view.document, currentId: view.page.id, readOnly: locked, onSelect: view.setPage,
    onAdd: locked ? null : addPageAt, onMove: locked ? null : editor.movePage, onMenu: locked ? null : openPageMenu } : null;

  const context = useMemo(() => ({ editor: view, brandKit, onSaveBrandKit, images, readOnly: locked, uploadImages, paintSelection, openLayerMenu, dock,
    onAskAI: onAskAI || null, imageGeneration: !!imageGeneration, videos: videoOn ? videos : null, videoUploads, openVideo, trimVideo }),
    [view, brandKit, onSaveBrandKit, images, locked, uploadImages, paintSelection, openLayerMenu, dock, onAskAI, imageGeneration, videoOn, videos, videoUploads, openVideo, trimVideo]);
  const onKeyDown = useShortcuts(view, { locked, scale, dock, playback });
  // Text running off any page, each flagged with its page (the download lists them), and the
  // image places still empty (2.10.0).
  const overflow = view.document ? view.document.pages.flatMap(page => overflowingLayers(page, view.document).map(layer => ({ ...layer, pageId: page.id }))) : [];
  const emptyPlaces = view.document ? emptyImagePlaces(view.document) : [];
  const ratio = ASPECT_RATIOS[placeholderRatio] || ASPECT_RATIOS['4:5'];
  // No header bar: the rail carries the version, the save status, history and zoom.
  const zoom = view.document ? { label: `${Math.round(scale * 100)}%`, onIn: () => editor.setZoom(nextZoom(scale, 1)),
    onOut: () => editor.setZoom(nextZoom(scale, -1)), onFit: () => editor.setZoom('fit') } : null;
  const panel = <ToolPanel tool={locked ? null : editor.tool} mode={editor.toolMode} placement={panelPlacement} onClose={() => editor.setTool(null)} />;
  const history = preview ? null : { canUndo: editor.canUndo, canRedo: editor.canRedo, undo: editor.undo, redo: editor.redo };

  return <ArtEditorContext.Provider value={context}>
    <div className={`art-editor ${docking ? 'has-dock' : ''} ${className}`} tabIndex={-1} onKeyDown={onKeyDown} aria-busy={!!busy || undefined}>
      <ToolRail active={locked ? null : editor.tool} onToggle={tool => editor.setTool(tool)} disabled={locked || !view.document}
        versions={versions} status={preview ? null : status} history={history} zoom={zoom} hidden={videoOn ? [] : ['videos']} />
      <section className="art-editor__workspace" aria-label={t('editor')}>
        {notice}
        {conflict && <div className="art-editor__notice" role="alert">
          <Text.caption contrast={8} left={<IconAlertCircle />}>{t('conflict')}</Text.caption>
          <div className="art-editor__notice-actions">
            <Button size="small" color="inverse" variant="rounded" preventLoading onClick={conflict.onKeep}>{t('keepMine')}</Button>
            <Button size="small" color="overlay" variant="rounded" preventLoading onClick={conflict.onLoad}>{t('loadSaved')}</Button>
          </div>
        </div>}
        <div className="art-editor__canvas">
          {panelPlacement !== 'outside' && panel}
          {view.document
            ? <Stage editor={view} readOnly={locked} highlight={highlight} onScale={setScale} onDropFiles={(files, at, frame) => uploadImages(frame ? files.slice(0, 1) : files, frame ? { target: frame } : { at })} onLayerMenu={openLayerMenu}
              playback={playback} onDropVideo={videoOn && !locked ? file => openVideo({ file }) : null} />
            : <div className="art-editor__placeholder"><span className="art-editor__placeholder-board art-skeleton" style={{ '--ratio': ratio.height / ratio.width, aspectRatio: `${ratio.width} / ${ratio.height}` }} /></div>}
          {view.document && !preview && <Inspector onAskAI={onAskAI} />}
          {view.cropping && <div className="art-editor__crop" role="status">
            <Text.caption contrast={8} left={<IconArrowsMove />}>{t('moveImageHint')}</Text.caption>
            <Button size="small" color="inverse" variant="rounded" preventLoading onClick={() => editor.crop(null)}>{t('done')}</Button>
          </div>}
          {busy && <Busy busy={busy} />}
          <Notices notices={notices} onDismiss={id => setNotices(list => list.filter(item => item.id !== id))} />
        </div>
        {view.page && pageVideos(view.page).length > 0 && <Timeline page={view.page} playback={playback} selectedId={view.selection.length === 1 ? view.selection[0] : null}
          readOnly={locked} onSelect={id => view.select([id])} onMove={(id, patch) => editor.update(id, patch, { key: null, label: 'clip' })}
          onRetrim={(id, patch) => editor.apply(doc => retrimClip(doc, id, patch), { label: 'trim' })} onTrim={trimVideo} />}
      </section>
      {panelPlacement === 'outside' && panel}
      <aside ref={node => { dockRef.current = node; setDockNode(node); }} className="art-dock" aria-label={t('settings')} hidden={!docking} />
      <ContextMenu ref={menu} />
      <ActionRail doc={view.document} pageId={view.page?.id} profile={profile} exporter={exporter} overflow={overflow} emptyPlaces={emptyPlaces}
        onSelectLayer={layer => { view.setPage(layer.pageId); view.select([layer.id]); }}
        caption={caption} onCaptionChange={preview ? null : onCaptionChange} actions={actions} close={close} pages={pages}
        onSaveTemplate={gallery?.create && view.document && !busy && !preview ? openSaveTemplate : null} />
      {adding !== null && gallery && <PageModels open onClose={() => setAdding(null)} source={gallery} onPick={insertModel}
        place={placeOf(view.document, adding)} ratio={view.document ? `${view.document.width} / ${view.document.height}` : undefined}
        onBlank={() => { const doc = latest.current.document; insertPages([blankPage(doc, doc.pages[adding - 1])]); }} />}
      {trim && <VideoTrimDialog open video={trim.video} mode={trim.mode} initial={trim.initial || null} background={trim.background} muted={trim.muted} limits={trimLimits}
        upload={trim.upload} onRetryUpload={trim.file ? () => uploadVideo(trim.key, trim.file, { width: trim.meta.width, height: trim.meta.height, duration: trim.meta.duration, mimeType: trim.file.type }) : null}
        onConfirm={confirmTrim} onClose={closeTrim} />}
      {savingTemplate && <TemplateDialog open mode="save" onClose={() => setSavingTemplate(null)} document={savingTemplate.document} format={savingTemplate.document.format}
        formatName={id => formatNameOf(t, id)} categories={savingTemplate.categories} initial={{ name: (title || '').slice(0, 80) }} onSubmit={saveTemplate} />}
    </div>
  </ArtEditorContext.Provider>;
}
